import * as React from 'react'
import * as _ from 'lodash'
import {
  IPayment,
  ITransaction,
  PaymentTypeIds,
  getLoyaltyMetaDataFields,
  paymentNameFormatter,
} from '@getgreenline/homi-shared'
import { Currency } from '../../../components/Currency'
import { CustomerViewAndEditModal } from '../../CustomerViewAndEditModal'
import { PaymentLine } from './PaymentLine'
import { Tooltip, Icon } from 'antd'
import { getFullNotes } from '../../../utilities/helpers'
import { BLAZE_COMPANY } from '../../../constants/general'
import { LoyaltyModels } from '@getgreenline/loyalties'

interface Props {
  payment: IPayment
  canEditCustomer: boolean
  canViewCustomerTotalSpend: boolean
  locationId: number | undefined
  loyaltyData?: LoyaltyModels.ILoyaltyData
  paymentOptionNames?: { [key: number]: string }
}

interface State {
  showCustomerDetails: boolean
}

export class PaymentDetails extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      showCustomerDetails: false,
    }
  }

  private getLoyaltyMetaData = (transaction: ITransaction) => {
    if (transaction.paymentTypeId !== PaymentTypeIds.loyalty) {
      return undefined
    }

    return getLoyaltyMetaDataFields(transaction)
  }

  render() {
    const { payment, locationId, loyaltyData, paymentOptionNames } = this.props
    const totalDepositFee = payment.paymentLines.reduce(
      (a, pl) => (pl.depositFee ? pl.depositFee + a : a),
      0,
    )

    const fullNotes = getFullNotes(
      true,
      payment.notes ? payment.notes : undefined,
      payment.paymentQueueNotes ? payment.paymentQueueNotes : undefined,
    )

    const isRefundPayment = loyaltyData && (loyaltyData.loyaltyReversed || 0) < 0

    return (
      <table className='table table-sm'>
        <tbody>
          <tr>
            <td>Sale ID</td>
            <td className='text-right' colSpan={2}>
              {payment.customId}
            </td>
          </tr>
          <tr>
            <td>Location</td>
            <td className='text-right' colSpan={2}>
              {payment.locationName}
            </td>
          </tr>
          <tr>
            <td>Employee</td>
            <td className='text-right' colSpan={2}>
              {payment.employeeName}
            </td>
          </tr>
          {payment.customerName !== null && (
            <tr>
              <td>Customer</td>
              <td className='text-right' colSpan={2}>
                <a onClick={() => this.setState({ showCustomerDetails: true })}>
                  {payment.customerName}
                </a>
              </td>
            </tr>
          )}
          <tr>
            <td>
              <b>Products</b>
            </td>
            <td></td>
            <td></td>
          </tr>
          {payment.paymentLines.map((paymentLine, index) => {
            return (
              <PaymentLine
                key={paymentLine.id}
                paymentLine={paymentLine}
                loyaltyData={loyaltyData}
              />
            )
          })}
          <tr>
            <td>
              <b>Summary</b>
            </td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td>Subtotal</td>
            <td></td>
            <td className='text-right'>
              <Currency centValue={payment.subTotal} />
            </td>
          </tr>
          {_.chain(payment.taxPaymentLines)
            .groupBy('taxId')
            .map((objs, key) => ({
              id: key,
              name: objs[0].taxName,
              totalAmount: objs.reduce((prev, next) => prev + next.amount, 0),
            }))
            .value()
            .map((aggregatedTax) => (
              <tr key={aggregatedTax.id}>
                <td>{aggregatedTax.name}</td>
                <td></td>
                <td className='text-right'>
                  <Currency centValue={aggregatedTax.totalAmount} />
                </td>
              </tr>
            ))}
          {!!totalDepositFee && (
            <tr>
              <td>Deposit Fees</td>
              <td></td>
              <td className='text-right'>
                <Currency centValue={totalDepositFee} />
              </td>
            </tr>
          )}
          <tr>
            <td>
              <b>Total</b>
            </td>
            <td></td>
            <td className='text-right'>
              <Currency centValue={payment.total} />
            </td>
          </tr>
          {payment.pennyAdjustment !== 0 && (
            <tr>
              <td>Penny adjustment</td>
              <td></td>
              <td className='text-right'>
                <Currency centValue={payment.pennyAdjustment} />
              </td>
            </tr>
          )}
          {payment.tendered !== 0 && (
            <tr>
              <td>Tendered</td>
              <td></td>
              <td className='text-right'>
                <Currency centValue={payment.tendered} />
              </td>
            </tr>
          )}
          {payment.paymentLinesTotalDiscrepancy !== null &&
            payment.paymentLinesTotalDiscrepancy !== 0 && (
              <tr>
                <td>
                  Online order discrepancy&nbsp;
                  <Tooltip
                    title={
                      <div>
                        {`The prices or taxes did not reconcile the amounts calculated by ${BLAZE_COMPANY}.
                        Please contact ${payment.externalSourceName} about Order ${payment.externalOrderId}`}
                      </div>
                    }
                  >
                    <Icon type='question-circle' />
                  </Tooltip>
                </td>
                <td></td>
                <td className='text-right'>
                  <Currency centValue={payment.paymentLinesTotalDiscrepancy} />
                </td>
              </tr>
            )}
          {payment.transactions.map((trans) => {
            const loyaltyMetaData = this.getLoyaltyMetaData(trans)

            return (
              <tr key={trans.id}>
                <td>
                  Paid with{' '}
                  {paymentOptionNames?.[trans.paymentTypeId] ||
                    paymentNameFormatter(trans.paymentTypeId)}
                </td>
                <td colSpan={2} className='text-right'>
                  <Currency centValue={trans.amount} />

                  {loyaltyData && loyaltyMetaData && loyaltyMetaData.points > 0 && (
                    <>
                      <br />
                      {isRefundPayment ? '' : '-'}
                      {loyaltyMetaData.points.toLocaleString('en-CA')} Points
                    </>
                  )}
                </td>
              </tr>
            )
          })}

          {loyaltyData && loyaltyData.loyaltyEarned !== undefined && (
            <tr>
              <td>{isRefundPayment ? 'Loyalty Returned' : 'Loyalty Earned'}</td>
              <td colSpan={2} className='text-right'>
                {loyaltyData.loyaltyEarned.toLocaleString('en-CA')} Points
              </td>
            </tr>
          )}

          {loyaltyData && loyaltyData.loyaltyReversed !== undefined && (
            <tr>
              <td>Loyalty Reversed</td>
              <td colSpan={2} className='text-right'>
                {loyaltyData.loyaltyReversed.toLocaleString('en-CA')} Points
              </td>
            </tr>
          )}

          {loyaltyData && loyaltyData.loyaltyBalance !== undefined && (
            <tr>
              <td>Loyalty Balance</td>
              <td colSpan={2} className='text-right'>
                {loyaltyData.loyaltyBalance.toLocaleString('en-CA')} Points
              </td>
            </tr>
          )}

          {fullNotes ? (
            <tr>
              <td>Notes</td>
              <td></td>
              <td style={{ maxWidth: 300 }} className={'gr-sales-notes'}>
                {fullNotes}
              </td>
            </tr>
          ) : undefined}
        </tbody>

        {this.state.showCustomerDetails && (
          <CustomerViewAndEditModal
            customerId={payment.customerId!}
            onHide={() => this.setState({ showCustomerDetails: false })}
            onSubmit={() => this.setState({ showCustomerDetails: false })}
            canSaveCustomer={this.props.canEditCustomer}
            canViewCustomerTotalSpend={this.props.canViewCustomerTotalSpend}
            customerLocationId={locationId}
          />
        )}
      </table>
    )
  }
}
